/**
 * NotificationProvider — 인앱 알림 수신·표시 상태 (Shell 전속)
 *
 * 소유: 프레임 팀. 에이전트 수정 금지.
 * 표준: F-03 §7.6 (수신측 표출 동작), C-04 §9 (SSE).
 *
 * 동작:
 *  1) 마운트 시 REST 로 미읽음 카운트 초기 적재 (배지 진실값)
 *  2) SSE 연결: GET /api/hermes/sse/notifications?token={accessToken}
 *     - event:notification → 배지 +1, 토스트 표출 (notiId 중복 제거)
 *     - event:heartbeat    → keepalive (무시)
 *  3) 연결/재연결(onopen) 시 REST 로 미읽음 카운트 재동기화 (끊긴 동안 누락분 보정 — §16 금지15)
 *  4) 탭 비포커스 시 토스트는 표출하지 않음(배지/카운트만 반영). OS 알림은 판단 보류(2026-06-21).
 *
 * 진실값 원칙: 배지 카운트의 원본은 REST(DB). SSE 는 증분 트리거일 뿐 (C-04 §9.3).
 */
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuthStore } from '@indipia/shared';
import type { NotificationItem } from './types';
import { fetchUnreadCount, markNotificationRead } from './notificationApi';

interface ToastEntry {
  uid: string;
  item: NotificationItem;
}

interface NotificationContextValue {
  unreadCount: number;
  toasts: ToastEntry[];
  dismissToast: (uid: string) => void;
  /** 토스트 [확인하기] — 읽음 처리 후 배지 보정 */
  markRead: (id: number) => void;
  /** H-1 모두읽음 후 배지 즉시 0 초기화 */
  resetUnreadCount: () => void;
  /** H-1 개별읽음 후 배지 -1 */
  decrementUnreadCount: () => void;
  /** [DEV 전용] 목 알림 1건을 실제 수신 경로와 동일하게 표출 (SSE/백엔드 불요) */
  pushMockToast: () => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

const SSE_RECONNECT_MS = 3000; // C-04 §9.2 / EventSource 기본 재연결 간격 (백오프 최초값)
const SSE_RECONNECT_MAX_MS = 60_000; // 백오프 상한 — 만료 토큰 등 자가회복 불가 상태의 폭주 차단
const TOAST_MAX = 5;           // 동시 표출 토스트 상한 (스택)

export function NotificationProvider({ children }: { children: ReactNode }) {
  const memberId = useAuthStore((s) => s.user?.memberId ?? null);
  const accessToken = useAuthStore((s) => s.accessToken);

  const [unreadCount, setUnreadCount] = useState(0);
  const [toasts, setToasts] = useState<ToastEntry[]>([]);

  const seenIds = useRef<Set<number>>(new Set()); // notiId 중복 제거 (§10.2)
  const esRef = useRef<EventSource | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const backoffMs = useRef(SSE_RECONNECT_MS); // 연결 성공 시 초기값으로 리셋
  const closedRef = useRef(false);
  const mockSeq = useRef(-1); // [DEV] 목 알림용 음수 notiId (실데이터와 충돌 방지)

  const dismissToast = (uid: string) => {
    setToasts((prev) => prev.filter((t) => t.uid !== uid));
  };

  /** 수신 알림 1건을 3표면(배지·토스트) 규칙대로 반영 (SSE·목 공용) */
  const emitNotification = (item: NotificationItem) => {
    if (item == null || seenIds.current.has(item.id)) return; // 중복 제거
    seenIds.current.add(item.id);

    // 배지: 증분 +1 (진실값은 다음 resync 에서 보정)
    setUnreadCount((c) => c + 1);

    // 토스트: 탭 포커스 상태일 때만 표출 (F-03 §7.6)
    if (document.visibilityState === 'visible') {
      const entry: ToastEntry = { uid: `${item.id}-${Date.now()}`, item };
      setToasts((prev) => [entry, ...prev].slice(0, TOAST_MAX));
    }
  };

  /** [DEV 전용] 목 알림 생성 → 실제 수신 경로(emitNotification)와 동일하게 표출 */
  const pushMockToast = () => {
    const id = mockSeq.current--;
    emitNotification({
      id,
      groupId: null,
      sourceAgent: 'work-log',
      notiType: 'SITE_REPORT_RECEIVED',
      title: '박기사 · 서초동 래미안',
      body: '욕실 방수 2차 작업 완료. 24시간 양생 후 타일 시공 가능합니다. 자재 추가 필요.',
      targetType: 'SITE_REPORT',
      targetId: 1001,
      linkUrl: '/agents/work-log',
      webMenuCode: null,
      appMenuCode: null,
      read: false,
      readDtm: null,
      createdAt: new Date().toISOString(),
    });
  };

  const markRead = (id: number) => {
    void markNotificationRead(id)
      .then(() => setUnreadCount((c) => Math.max(0, c - 1)))
      .catch(() => {
        /* 실패 시 다음 재동기화에서 REST 진실값으로 보정됨 */
      });
  };

  useEffect(() => {
    if (!memberId || !accessToken) return;
    closedRef.current = false;

    // REST 초기/재동기화 — 배지 진실값
    const resync = () => {
      void fetchUnreadCount(memberId)
        .then(setUnreadCount)
        .catch(() => {
          /* 일시 실패는 다음 resync 에서 보정 */
        });
    };

    const handleNotification = (ev: MessageEvent) => {
      let item: NotificationItem;
      try {
        item = JSON.parse(ev.data) as NotificationItem;
      } catch {
        return;
      }
      emitNotification(item);
    };

    const connect = () => {
      if (closedRef.current) return;
      // 토큰은 effect 클로저가 아니라 스토어에서 매번 읽는다. 클로저 값을 쓰면 만료 후 재연결이
      // 같은 만료 토큰으로 영원히 401을 반복한다(EventSource 는 axios 를 타지 않아 refresh
      // 인터셉터가 동작하지 않으므로 스스로 회복할 수 없다).
      const token = useAuthStore.getState().accessToken;
      if (!token) return; // 로그아웃 등 — 재연결 중단. 재로그인 시 effect 가 다시 돈다.
      const base = import.meta.env.VITE_API_BASE || '';
      const url = `${base}/api/hermes/sse/notifications?token=${encodeURIComponent(token)}`;
      const es = new EventSource(url);
      esRef.current = es;

      es.onopen = () => {
        backoffMs.current = SSE_RECONNECT_MS; // 정상 연결 — 백오프 리셋
        resync(); // 연결/재연결 시 REST 재동기화
      };
      es.addEventListener('notification', (ev) => handleNotification(ev as MessageEvent));
      es.addEventListener('heartbeat', () => {/* keepalive */});

      es.onerror = () => {
        es.close();
        esRef.current = null;
        if (closedRef.current) return;
        if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
        // EventSource 는 상태코드를 노출하지 않아 401·네트워크 장애를 구분할 수 없다.
        // 원인 불문 지수 백오프로 재시도 빈도를 낮춘다(상한 60초).
        const delay = backoffMs.current;
        backoffMs.current = Math.min(delay * 2, SSE_RECONNECT_MAX_MS);
        reconnectTimer.current = setTimeout(connect, delay);

        // 자가회복 트리거 — resync 는 axios 를 타므로 토큰이 만료됐다면 401 → refresh 인터셉터가
        // 갱신하고 스토어를 바꾼다. 그러면 이 effect 가 재실행되며 새 토큰으로 재연결된다.
        // refresh 마저 실패하면 인터셉터가 로그아웃시켜(기존 정책) effect 가드가 재연결을 멈춘다.
        // 이 호출이 없으면 만료 토큰으로 영원히 재시도만 반복한다.
        resync();
      };
    };

    resync();   // SSE 연결 전 우선 1회
    connect();

    return () => {
      closedRef.current = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      esRef.current?.close();
      esRef.current = null;
    };
  }, [memberId, accessToken]);

  const resetUnreadCount = () => setUnreadCount(0);
  const decrementUnreadCount = () => setUnreadCount((c) => Math.max(0, c - 1));

  return (
    <NotificationContext.Provider value={{ unreadCount, toasts, dismissToast, markRead, resetUnreadCount, decrementUnreadCount, pushMockToast }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications(): NotificationContextValue {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    // Provider 밖에서 호출되면 안전한 기본값 (배지 0)
    return { unreadCount: 0, toasts: [], dismissToast: () => {}, markRead: () => {}, resetUnreadCount: () => {}, decrementUnreadCount: () => {}, pushMockToast: () => {} };
  }
  return ctx;
}
